import { Box, Grid2 } from '@mui/material';
import React, { useEffect } from 'react';
import CustomButton from '../lib/ui-components/Button/CustomButton';
import { Formik } from 'formik';
import * as Yup from 'yup';
import './CompleteConsultModel.css';
import {
  type CompleteDDLData,
  type CompleteConsultParams,
} from './statusRequestService';
import CustomDialog from '../lib/ui-components/Dialog/CustomDialog';
import CustomDialogContent from '../lib/ui-components/Dialog/CustomDialogContent';
import ConsultCompleteIcon from '../../assets/images/ConsultComplete-pop.svg';

import CustomTypography from '../lib/ui-components/Typography/CustomTypography';
import CustomRadioGroup from '../lib/ui-components/RadioGroup/CustomRadioGroup';
import {
  CustomAutoComplete,
  CustomCheckBox,
  CustomDialogActions,
  CustomSelect,
  CustomTextField,
} from '../..';

const rgoptions = [
  { label: 'Billable', value: '0' },
  { label: 'Non-Billable', value: '1' },
];

export interface CompleteConsultModelProps {
  openCompletePopup: boolean;
  onCloseCompletePopup: () => void;
  completeDDLDetails: CompleteDDLData | undefined;
  onSubmitCompleteConsult: (data: CompleteConsultParams) => void;
  portalType: string;
  initialValues?: CompleteConsultParams | null;
}

const CompleteConsultModel: React.FC<CompleteConsultModelProps> = ({
  openCompletePopup,
  onCloseCompletePopup,
  completeDDLDetails,
  onSubmitCompleteConsult,
  portalType,
  initialValues,
}) => {
  const completeInitialValues: CompleteConsultParams = {
    userId: 0,
    consultId: 0,
    billable: true,
    diagnosis: [],
    recommendation: 0,
    emCodes: 0,
    isSurge: false,
    opsReasonForCompletion: '',
    unitOrDepartment: completeDDLDetails?.selectedUnitOrDepartment ?? '',
    followUp: 0,
  };

  const [completeConsultValues, setCompleteConsultValues] =
    React.useState<CompleteConsultParams>(completeInitialValues);

  const departmentOptions = completeDDLDetails?.unitOrDepartment.map(
    (item) => ({
      id: item,
      name: item,
    })
  );
  useEffect(() => {
    setCompleteConsultValues((prev) => {
      if (initialValues && Object.keys(initialValues).length > 0) {
        return initialValues;
      }

      const unitOrDepartment =
        completeDDLDetails?.selectedUnitOrDepartment ?? '';
      return {
        ...prev,
        unitOrDepartment,
      };
    });
  }, [initialValues, completeDDLDetails]);

  const validationSchema = Yup.object().shape({
    billable: Yup.boolean().default(true),

    diagnosis: Yup.array().when('billable', {
      is: true,
      then: (schema) =>
        schema
          .of(
            Yup.object().shape({
              id: Yup.number().required(),
              name: Yup.string().required(),
            })
          )
          .min(1, 'Diagnosis is required'),
      otherwise: (schema) => schema,
    }),

    recommendation: Yup.number().when('billable', {
      is: true,
      then: (schema) =>
        schema
          .min(1, 'Recommendations is required')
          .required('Recommendations is required'),
      otherwise: (schema) => schema.notRequired(),
    }),

    emCodes: Yup.number().when('billable', {
      is: true,
      then: (schema) =>
        schema.min(1, 'EM Codes is required').required('EM Codes is required'),
      otherwise: (schema) => schema.notRequired(),
    }),

    opsReasonForCompletion:
      portalType.toLocaleUpperCase() === 'OPERATIONS'
        ? Yup.string()
            .max(500, 'Maximum 500 characters allowed')
            .required('Comment is required')
        : Yup.string().notRequired(),
  });
  return (
    <Box className="pr-12">
      <Formik
        initialValues={completeConsultValues}
        validateOnMount={false}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={(values) => {
          if (values.billable) {
            onSubmitCompleteConsult(values);
          } else {
            onSubmitCompleteConsult({
              ...values,
              diagnosis: [],
              recommendation: 0,
              emCodes: 0,
            });
          }
        }}
      >
        {({
          setFieldValue,
          values,
          handleSubmit,
          resetForm,
          errors,
          touched,
        }) => {
          return (
            <Box className="pr-12">
              <CustomDialog
                id="dialogWarning"
                open={openCompletePopup}
                onClose={() => {
                  onCloseCompletePopup();
                  resetForm();
                }}
                disableEscapeKeyDown={true}
              >
                <CustomDialogContent
                  sx={{ width: '420px' }}
                  className="card-width-popup"
                >
                  <Box className="pb-12 flex-justify-space-between">
                    <ConsultCompleteIcon />
                  </Box>
                  <Box className="pb-4">
                    <CustomTypography
                      id="Complete-title-popup"
                      variant="subtitle1"
                    >
                      Complete Consult?
                    </CustomTypography>
                  </Box>
                  <Box>
                    <CustomTypography id="Complete-text-popup" variant="body3">
                      Are you sure you want to mark this consult as completed?
                      This action cannot be undone
                    </CustomTypography>
                  </Box>
                  <Box className="mt-16">
                    <CustomRadioGroup
                      id="radioGroup"
                      options={rgoptions}
                      selectedValue={values.billable ? '0' : '1'}
                      onChange={(item) => {
                        const isBillable = item === '0';
                        setFieldValue('billable', isBillable);
                        if (!isBillable) {
                          setFieldValue('diagnosis', []);
                          setFieldValue('otherDiagnosis', '');
                          setFieldValue('recommendation', 0);
                          setFieldValue('emCodes', 0);
                        }
                      }}
                      color="primary"
                      size="small"
                      row
                    />
                  </Box>

                  {values.billable && (
                    <>
                      <Box className="mt-16">
                        <CustomAutoComplete
                          id="complete-diagnosis-ddl"
                          placeholder="Choose Diagnosis"
                          size="small"
                          options={completeDDLDetails?.diagnosis ?? []}
                          value={values.diagnosis}
                          onChange={(selectedOptions) => {
                            setFieldValue('diagnosis', selectedOptions);
                          }}
                          disableCloseOnSelect={true}
                        />
                        {errors?.diagnosis && touched?.diagnosis && (
                          <CustomTypography
                            id="validate-diagnosis"
                            color="error"
                            variant="body3"
                            className="pt-8"
                          >
                            {typeof errors.diagnosis === 'string' &&
                              errors.diagnosis}
                          </CustomTypography>
                        )}
                      </Box>

                      <Box className="mt-16">
                        <CustomSelect
                          id="complete-emcodes-ddl"
                          options={completeDDLDetails?.emCodes ?? []}
                          selectedValue={values.emCodes}
                          onChange={(data) => {
                            const selectedText =
                              completeDDLDetails?.emCodes.find(
                                (option) => option.id === data
                              )?.id;
                            setFieldValue('emCodes', selectedText);
                          }}
                          size="small"
                          displayEmpty={true}
                          placeholder="Choose Procedure (E/M Codes)"
                          fullWidth
                          menuItemClassName="emcodes-menu-item"
                        />
                        {errors?.emCodes && touched?.emCodes && (
                          <CustomTypography
                            id="validate-emcodes"
                            color="error"
                            variant="body3"
                            className="pt-8"
                          >
                            {errors.emCodes}
                          </CustomTypography>
                        )}
                      </Box>

                      <Box className="mt-16">
                        <CustomSelect
                          id="complete-recommendations-ddl"
                          options={completeDDLDetails?.recommendations ?? []}
                          selectedValue={values.recommendation}
                          onChange={(data) => {
                            const selectedText =
                              completeDDLDetails?.recommendations.find(
                                (option) => option.id === data
                              )?.id;
                            setFieldValue('recommendation', selectedText);
                          }}
                          size="small"
                          displayEmpty={true}
                          placeholder="Choose Recommendations"
                          fullWidth
                        />
                        {errors?.recommendation && touched?.recommendation && (
                          <CustomTypography
                            id="validate-recommendation"
                            color="error"
                            variant="body3"
                            className="pt-8"
                          >
                            {errors.recommendation}
                          </CustomTypography>
                        )}
                      </Box>
                    </>
                  )}

                  <Box className="mt-16 mb-12">
                    <CustomSelect
                      id="complete-unit-department-ddl"
                      options={departmentOptions ?? []}
                      selectedValue={values.unitOrDepartment}
                      onChange={(data) => {
                        const selectedValue =
                          typeof data === 'string' ? data : String(data);
                        setFieldValue('unitOrDepartment', selectedValue);
                      }}
                      size="small"
                      displayEmpty={true}
                      placeholder="Choose ED/IP"
                      fullWidth
                    />
                  </Box>
                  <Box className="mt-16 mb-12">
                    <CustomSelect
                      id="complete-followup-ddl"
                      options={completeDDLDetails?.followUp ?? []}
                      selectedValue={values.followUp}
                      onChange={(data) => {
                        const selectedValue = completeDDLDetails?.followUp.find(
                          (option) => option.id === data
                        )?.id;
                        setFieldValue('followUp', selectedValue);
                      }}
                      size="small"
                      displayEmpty={true}
                      placeholder="Choose Follow-Up"
                      fullWidth
                    />
                  </Box>
                  <Box className="text-align-left mt-4">
                    <CustomCheckBox
                      id="txt-prescriber-medication"
                      size="medium"
                      checked={values.isSurge}
                      onChange={(event) => {
                        setFieldValue('isSurge', event.target.checked);
                      }}
                      label="Surge"
                      labelPlacement="end"
                    />
                  </Box>
                  {portalType.toUpperCase() === 'OPERATIONS' && (
                    <Box className="mt-16">
                      <CustomTextField
                        id="other reason"
                        value={values.opsReasonForCompletion}
                        placeholder="Operation Portal Comments"
                        size="small"
                        type="text"
                        className="mr-10"
                        onChange={(e) => {
                          setFieldValue(
                            'opsReasonForCompletion',
                            e.target.value
                          );
                        }}
                        multiLine={true}
                      />
                      {errors?.opsReasonForCompletion &&
                        touched?.opsReasonForCompletion && (
                          <CustomTypography
                            id="validate-otherCancelReason"
                            color="error"
                            variant="body3"
                            className="pt-8"
                          >
                            {errors.opsReasonForCompletion}
                          </CustomTypography>
                        )}
                    </Box>
                  )}
                </CustomDialogContent>

                <CustomDialogActions>
                  <Grid2 container>
                    <Grid2 className="mr-8">
                      <CustomButton
                        id="btnYes"
                        color="error"
                        variant="outlined"
                        onClick={() => {
                          onCloseCompletePopup();
                          resetForm();
                        }}
                      >
                        Cancel
                      </CustomButton>
                    </Grid2>
                    <Grid2>
                      <CustomButton
                        id="btnConfirm-complete"
                        color="primary"
                        variant="contained"
                        onClick={() => {
                          handleSubmit();
                        }}
                      >
                        Submit
                      </CustomButton>
                    </Grid2>
                  </Grid2>
                </CustomDialogActions>
              </CustomDialog>
            </Box>
          );
        }}
      </Formik>
    </Box>
  );
};

export default CompleteConsultModel;
